import React, {ChangeEvent, FC, useState} from 'react';
import clsx from 'clsx'; // Import clsx for class concatenation

interface FormFieldProps {
    label?: string;
    value?: string;
    type?: string; // TODO: Change this to an enum of available types
    multipleFiles?: boolean;
    placeholder?: string;
    disabled?: boolean;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    className?: string;
    required?: boolean;
    checked?: boolean;
    name?: string;
    labelPosition?: 'left' | 'right' | 'top';
    error?: any;
    icon?: React.ReactNode;
    hideLabel?: boolean
    inputIcon?: React.ReactNode;
    pattern?: any,
    title?: any,
    baseClass?: boolean,
    accept?: any,
    id?: any,
    maxLength?: number;
    minLength?: any
    step?: any,
    labelClassName?: any

}

const InputField: FC<FormFieldProps> = ({
                                            label,
                                            value,
                                            type = 'text',
                                            multipleFiles = false,
                                            placeholder = '',
                                            disabled = false,
                                            required = false,
                                            checked = false,
                                            onChange,
                                            onClick,
                                            className = '',
                                            name,
                                            labelPosition = 'left',
                                            error,
                                            icon,
                                            hideLabel = false,
                                            inputIcon,
                                            pattern,
                                            title,
                                            baseClass = true,
                                            accept,
                                            id,
                                            maxLength,
                                            minLength,
                                            step,
                                            labelClassName
                                        }) => {
    const [showPassword, setShowPassword] = useState(false);

    // Function to render the input field
    const renderInputField = () => (
        <>
            <input
                type={showPassword && type === 'password' ? 'text' : type}
                multiple={multipleFiles}
                step={type === 'number' ? 'any' : ''}
                id={id}
                checked={checked}
                placeholder={placeholder || `Enter ${label}...`}
                value={value}
                onChange={onChange}
                disabled={disabled}
                required={required}
                onClick={onClick}
                name={name}
                pattern={pattern}
                accept={accept}
                title={title}
                maxLength={maxLength}
                minLength={minLength}

                className={clsx(
                    'border-3 border-surface-5 text-surface-3 rounded-md p-2 placeholder-text-2',
                    type === 'checkbox' || type === 'radio' ? '' : 'w-[100%]',
                    labelPosition !== 'top' && 'mt-2',
                    disabled
                        ? 'cursor-text border border-surface-5 bg-surface-5 text-[#495057]'
                        : 'border border-surface-5 text-sm',
                    className
                )}
            />

            {inputIcon &&
                inputIcon
            }
            {type === 'password' && value && (
                <i
                    className={clsx(
                        'fa cursor-pointer text-surface-0 absolute left-auto right-5 top-[18px]',
                        showPassword ? 'fa-eye' : 'fa-eye-slash'
                    )}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-hidden="true"
                />
            )}
            {error && <div className="text-red-500 text-sm">{error}</div>}
        </>
    );

    // Function to render the label and optional icon
    const renderLabel = () => (
        <div className="flex items-center">
            {label && (
                <span className={'text-surface-0 '}>
                    {required ? `*${label}` : label}
                </span>
            )}
            {icon && <span className="ml-2">{icon}</span>}
        </div>
    );

    const baseClasses = baseClass ? 'col-span-12 grid grid-cols-12 gap-3' : "";
    const labelClasses = `${labelClassName} col-span-3 sm:mt-2 lg:mt-0 mt-2 flex items-center`;
    const inputClasses = `relative ${hideLabel ? 'col-span-12' : (type !== "radio" ? 'col-span-9' : 'col-span-1')}`;
    // Layout based on labelPosition and type
    const layout = () => {

        switch (labelPosition) {
            case 'left':
                return (
                    <div className={baseClasses}>
                        {!hideLabel && <div className={labelClasses}>{renderLabel()}</div>}
                        <div className={inputClasses}>{renderInputField()}</div>
                    </div>
                );
            case 'right':
                return (
                    <div className={baseClasses}>
                        {!hideLabel && <div className={inputClasses}>{renderInputField()}</div>}
                        <div className={labelClasses}>{renderLabel()}</div>
                    </div>
                );
            case 'top':
                return (
                    <div className="col-span-12">
                        {!hideLabel && <div>{renderLabel()}</div>}
                        <div>{renderInputField()}</div>
                    </div>
                );
            default:
                return renderInputField();
        }
    };

    return (
        <>
            {type !== 'toggle' && layout()}

            {type === 'toggle' && (
                <div className="col-span-12 grid grid-cols-12 gap-3 mt-3">
                    <div className={labelClasses}>{renderLabel()}</div>
                    <div className='col-span-9 mt-1'>
                        <div
                            onClick={onClick}
                            className={clsx(
                                'cursor-pointer relative toggle_class w-11 h-6',
                                checked
                                    ? 'bg-highlight-0'
                                    : 'bg-surface-4',
                                'rounded-full transition-all'
                            )}>
                            <div
                                className={clsx(
                                    'cursor-pointer relative toggle_class w-11 h-6 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-text-0  after:absolute after:top-[2px] after:bg-text-1 after:border-gray-300 after:border after:border-solid after:border-1 after:border-white after:rounded-full after:h-5 after:w-5 after:transition-all after:bg-text-1 after:border-white',
                                    checked ? 'bg-highlight-0 after:end-[2px]' : 'bg-surface-4 after:start-[2px]'
                                )}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InputField;
